import { parseAnnotation } from '../model/parseAnnotation';

export interface BriefSectionCommentIndex {
  /** Comments per section, for the count the section shows at rest. */
  countsBySection: Record<string, number>;
  /** Which section each comment is anchored to, for scrolling a selected comment into view. */
  sectionByComment: Record<string, string>;
}

/**
 * Indexes a brief's comment annotations by section, across BOTH anchor kinds.
 *
 * The existing annotation selector narrows to `kind: 'text'` because it feeds TipTap
 * highlights, which only a range can produce. The document needs the wider view: a section
 * commented on through the section affordance alone has no range, and would otherwise report
 * no comments and have nothing to scroll to.
 *
 * A detached text anchor is deliberately asymmetric — navigable but not counted. Its text no
 * longer exists, so counting it would advertise a discussion the reader cannot locate, while
 * dropping it entirely would strand the comment with no way back to its section.
 */
export function buildBriefSectionCommentIndex(
  entries: { commentId: string; annotation: unknown }[],
): BriefSectionCommentIndex {
  const countsBySection: Record<string, number> = {};
  const sectionByComment: Record<string, string> = {};

  for (const { commentId, annotation } of entries) {
    const parsed = parseAnnotation(annotation);
    if (parsed?.kind !== 'text' && parsed?.kind !== 'field') continue;

    sectionByComment[commentId] = parsed.sectionId;
    if (parsed.kind === 'text' && parsed.detached) continue;
    countsBySection[parsed.sectionId] = (countsBySection[parsed.sectionId] ?? 0) + 1;
  }

  return { countsBySection, sectionByComment };
}
